import React, { useMemo } from 'react';
import {
    ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, Tooltip, CartesianGrid, Legend, LineChart, Line,
    ComposedChart,
} from 'recharts';
import { useNavigate } from 'react-router';
import { useAppContext } from './AppContext';
import DateRangeSlider from './DateRangeSlider';

interface SpendingChartsProps {
    onFilter?: (months: string[]) => void;
    onClear?: () => void;
}

const CHART_COLORS = ['#000', '#333', '#1a5f3c', '#806000', '#4d3a00', '#555', '#2d7a4f', '#777', '#40956b', '#999'];
const PIE_COLORS = ['#000', '#1a5f3c', '#806000', '#4d3a00', '#2d7a4f', '#555', '#40956b', '#777', '#5bb088', '#999'];

const TOOLTIP_STYLE = {
    contentStyle: {
        background: '#111',
        border: '1px solid rgba(255,204,0,0.3)',
        borderRadius: 10,
        fontFamily: 'inherit',
        fontSize: '0.72rem',
    },
    itemStyle: { color: '#fff' },
    labelStyle: { color: '#ffcc00', fontWeight: 700 },
} as const;

const AXIS_TICK = { fontSize: 10, fontFamily: 'inherit', fill: '#000' };

const fmtCurrency = (code: string) => ((v: any) => [`${code} ${Number(v).toLocaleString()}`, 'Spent']) as any;
const fmtCurrencyLabel = (code: string, label: string) => ((v: any) => [`${code} ${Number(v).toLocaleString()}`, label]) as any;

export default function SpendingCharts({ onFilter, onClear }: SpendingChartsProps) {
    const { appData } = useAppContext();
    const navigate = useNavigate();
    const receipts = appData.data.reportReceipts;
    const items = appData.data.reportProductItems;
    const merchants = appData.data.reportMerchants;
    const brands = appData.data.brands || [];
    const corporations = appData.data.corporations || [];

    // Use the user's configured home currency; fall back to the most common receipt currency
    const homeCurrency = appData.user.currency
        || receipts.find(r => r.currency_code)?.currency_code
        || 'EUR';

    // Returns the amount for a receipt expressed in the home currency.
    // If the receipt was in a foreign currency and has already been converted,
    // prefer home_total_amount; otherwise use total_amount as-is.
    const homeAmt = (r: any): number => {
        if (r.home_currency_code === homeCurrency && r.home_total_amount != null) {
            return r.home_total_amount;
        }
        return r.total_amount || 0;
    };

    // ── 1. Monthly spending trend ──────────────────────────────────────────
    const monthlySpending = useMemo(() => {
        const map = new Map<string, { total: number; count: number }>();
        receipts.forEach(r => {
            if (!r.transaction_date || !r.total_amount) return;
            const ym = r.transaction_date.substring(0, 7);
            const cur = map.get(ym) || { total: 0, count: 0 };
            cur.total += homeAmt(r);
            cur.count += 1;
            map.set(ym, cur);
        });
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return Array.from(map.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([ym, v]) => {
                const [year, month] = ym.split('-');
                return {
                    ym,
                    month: `${monthNames[parseInt(month) - 1]} ${year.slice(2)}`,
                    total: Math.round(v.total * 100) / 100,
                    receipts: v.count,
                };
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [receipts, homeCurrency]);

    // ── 2. Spending by category (product_type) ────────────────────────────
    const categorySpending = useMemo(() => {
        const map = new Map<string, number>();
        const displayName = new Map<string, string>();
        items.forEach(item => {
            const raw = item.product_type || 'Uncategorised';
            const key = raw.toLowerCase();
            if (!displayName.has(key)) displayName.set(key, raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase());
            map.set(key, (map.get(key) || 0) + (item.total_price || 0));
        });
        return Array.from(map.entries())
            .map(([key, value]) => ({ name: displayName.get(key) || key, value: Math.round(value * 100) / 100 }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 10);
    }, [items]);

    // ── 3. Top merchants by spend ──────────────────────────────────────────
    const topMerchants = useMemo(() => {
        return [...merchants]
            .filter(m => m.merchant_total_amount && m.name)
            .sort((a, b) => (b.merchant_total_amount || 0) - (a.merchant_total_amount || 0))
            .slice(0, 8)
            .map(m => ({
                id: m.merchant_id,
                name: m.name!.length > 18 ? m.name!.substring(0, 18) + '…' : m.name!,
                total: Math.round((m.merchant_total_amount || 0) * 100) / 100,
            }));
    }, [merchants]);

    // ── 4. Spending by day of week ─────────────────────────────────────────
    const dayOfWeekSpending = useMemo(() => {
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const totals = new Array(7).fill(0);
        const counts = new Array(7).fill(0);
        receipts.forEach(r => {
            if (!r.transaction_date || !r.total_amount) return;
            const d = new Date(r.transaction_date).getDay();
            totals[d] += homeAmt(r);
            counts[d] += 1;
        });
        return days.map((day, i) => ({
            day,
            total: Math.round(totals[i] * 100) / 100,
            avg: counts[i] > 0 ? Math.round((totals[i] / counts[i]) * 100) / 100 : 0,
            visits: counts[i],
        }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [receipts, homeCurrency]);

    // ── 5. Spending by brand (top 8) ───────────────────────────────────────
    const topBrands = useMemo(() => {
        const map = new Map<string, number>();
        items.forEach(item => {
            const brand = item.brand;
            if (!brand) return;
            map.set(brand, (map.get(brand) || 0) + (item.total_price || 0));
        });
        return Array.from(map.entries())
            .map(([name, value]) => ({
                fullName: name,
                name: name.length > 18 ? name.substring(0, 18) + '…' : name,
                total: Math.round(value * 100) / 100,
            }))
            .sort((a, b) => b.total - a.total)
            .slice(0, 8);
    }, [items]);

    // ── 6. Cumulative spending over time ───────────────────────────────────
    const cumulativeSpending = useMemo(() => {
        let running = 0;
        return monthlySpending.map(m => {
            running += m.total;
            return { month: m.month, cumulative: Math.round(running * 100) / 100 };
        });
    }, [monthlySpending]);

    // ── 7. Receipts + spending dual-axis per month ─────────────────────────
    const receiptsTrend = useMemo(() => {
        return monthlySpending.map(m => ({
            month: m.month,
            receipts: m.receipts,
            total: m.total,
        }));
    }, [monthlySpending]);

    // ── 8. Average basket size (items per receipt) per month ───────────────
    const basketTrend = useMemo(() => {
        const receiptItems = new Map<string, number>();
        const receiptMonth = new Map<string, string>();
        items.forEach(item => {
            if (!item.receipt_id || !item.receipt_transaction_date) return;
            receiptItems.set(item.receipt_id, (receiptItems.get(item.receipt_id) || 0) + 1);
            receiptMonth.set(item.receipt_id, item.receipt_transaction_date.substring(0, 7));
        });
        const monthMap = new Map<string, { totalItems: number; receiptCount: number }>();
        receiptItems.forEach((count, rid) => {
            const ym = receiptMonth.get(rid);
            if (!ym) return;
            const cur = monthMap.get(ym) || { totalItems: 0, receiptCount: 0 };
            cur.totalItems += count;
            cur.receiptCount += 1;
            monthMap.set(ym, cur);
        });
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return Array.from(monthMap.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([ym, v]) => {
                const [year, month] = ym.split('-');
                return {
                    month: `${monthNames[parseInt(month) - 1]} ${year.slice(2)}`,
                    avg: Math.round((v.totalItems / v.receiptCount) * 10) / 10,
                    items: v.totalItems,
                };
            });
    }, [items]);

    // ── 9. Data quality metrics ────────────────────────────────────────────
    const dataQuality = useMemo(() => {
        const missingBrand = items.filter(i => !i.brand || i.brand === 'Unknown').length;
        const missingCategory = items.filter(i => !i.product_type || i.product_type === 'Unknown').length;
        const zeroTotal = receipts.filter(r => !r.total_amount || r.total_amount <= 0).length;
        const missingDate = receipts.filter(r => !r.transaction_date).length;
        const missingCoords = merchants.filter(m => !m.latitude || !m.longitude).length;

        // Receipts where sum of item totals doesn't match receipt total
        const itemSumByReceipt = new Map<string, number>();
        items.forEach(i => {
            if (!i.receipt_id) return;
            itemSumByReceipt.set(i.receipt_id, (itemSumByReceipt.get(i.receipt_id) || 0) + (i.total_price || 0));
        });
        const totalMismatch = receipts.filter(r => {
            if (!r.receipt_id || !r.total_amount || r.total_amount <= 0) return false;
            const itemSum = itemSumByReceipt.get(r.receipt_id);
            if (itemSum == null) return false;
            return Math.abs(itemSum - r.total_amount) > 0.02;
        }).length;

        // Orphan detection
        const receiptIds = new Set(receipts.map(r => r.receipt_id).filter(Boolean));
        const merchantIds = new Set(merchants.map(m => m.merchant_id).filter(Boolean));
        const usedMerchantIds = new Set(receipts.map(r => r.merchant_id).filter(Boolean));
        const usedBrandNames = new Set(items.map(i => i.brand).filter(Boolean));
        const usedCorpNames = new Set(brands.map((b: any) => b.owning_corporation).filter(Boolean));

        const orphanedItems = items.filter(i => i.receipt_id && !receiptIds.has(i.receipt_id)).length;
        const orphanedReceipts = receipts.filter(r => r.merchant_id && !merchantIds.has(r.merchant_id)).length;
        const orphanedMerchants = merchants.filter(m => !usedMerchantIds.has(m.merchant_id)).length;
        const orphanedBrands = brands.filter((b: any) => b.name && !usedBrandNames.has(b.name)).length;
        const orphanedCorps = corporations.filter((c: any) => c.name && !usedCorpNames.has(c.name)).length;

        const total = receipts.length + items.length + merchants.length + brands.length + corporations.length;
        const issues = missingBrand + missingCategory + zeroTotal + missingDate + missingCoords
            + totalMismatch + orphanedItems + orphanedReceipts + orphanedMerchants + orphanedBrands + orphanedCorps;
        const score = total > 0 ? Math.round(((total - issues) / total) * 100) : 100;
        return {
            score,
            breakdown: [
                { issue: 'Missing brand', count: missingBrand },
                { issue: 'Missing category', count: missingCategory },
                { issue: 'Zero-total receipts', count: zeroTotal },
                { issue: 'Missing date', count: missingDate },
                { issue: 'Missing location', count: missingCoords },
                { issue: 'Total mismatch', count: totalMismatch },
            ].filter(d => d.count > 0),
            orphans: [
                { issue: 'Orphaned items', count: orphanedItems },
                { issue: 'Orphaned receipts', count: orphanedReceipts },
                { issue: 'Orphaned merchants', count: orphanedMerchants },
                { issue: 'Orphaned brands', count: orphanedBrands },
                { issue: 'Orphaned corporations', count: orphanedCorps },
            ].filter(d => d.count > 0),
        };
    }, [receipts, items, merchants, brands, corporations]);

    // ── 10. Spending by currency ───────────────────────────────────────────
    const currencyBreakdown = useMemo(() => {
        const map = new Map<string, number>();
        receipts.forEach(r => {
            const cur = r.currency_code || 'Unknown';
            map.set(cur, (map.get(cur) || 0) + (r.total_amount || 0));
        });
        return Array.from(map.entries())
            .map(([currency, total]) => ({ currency, total: Math.round(total * 100) / 100 }))
            .sort((a, b) => b.total - a.total);
    }, [receipts]);

    // ── 11. AI tokens used ─────────────────────────────────────────────────
    const totalTokens = useMemo(() => {
        return receipts.reduce((s, r) => s + ((r as any).tokens_used || 0), 0);
    }, [receipts]);

    // ── 12. Reconciliation rate ────────────────────────────────────────────
    const reconRate = useMemo(() => {
        const checked = receipts.filter(r => r.total_checks_out !== undefined);
        if (checked.length === 0) return null;
        const passed = checked.filter(r => r.total_checks_out === true).length;
        return Math.round((passed / checked.length) * 100);
    }, [receipts]);

    if (receipts.length === 0) return null;

    const currencyCode = homeCurrency;
    const totalSpend = Math.round(receipts.reduce((s, r) => s + homeAmt(r), 0) * 100) / 100;
    const avgPerReceipt = receipts.length > 0 ? Math.round((totalSpend / receipts.length) * 100) / 100 : 0;
    const uniqueMerchants = new Set(receipts.map(r => (r as any).merchant_id).filter(Boolean)).size;
    const uniqueBrands = new Set(items.map(i => i.brand).filter(Boolean)).size;

    const renderPieLabel = ({ name, percent }: any) =>
        percent > 0.04 ? `${name} ${(percent * 100).toFixed(0)}%` : '';

    return (
        <>
            {/* ── Summary row 1 ──────────────────────────────────────── */}
            <div className="chart-card chart-summary-row">
                <div className="chart-summary-stat">
                    <span className="chart-summary-value">{currencyCode} {totalSpend.toLocaleString()}</span>
                    <span className="chart-summary-label">Total Spend</span>
                </div>
                <div className="chart-summary-stat">
                    <span className="chart-summary-value">{receipts.length}</span>
                    <span className="chart-summary-label">Receipts</span>
                </div>
                <div className="chart-summary-stat">
                    <span className="chart-summary-value">{currencyCode} {avgPerReceipt.toLocaleString()}</span>
                    <span className="chart-summary-label">Avg / Receipt</span>
                </div>
                <div className="chart-summary-stat">
                    <span className="chart-summary-value">{items.length}</span>
                    <span className="chart-summary-label">Items</span>
                </div>
            </div>

            {/* ── Summary row 2 ──────────────────────────────────────── */}
            <div className="chart-card chart-summary-row">
                <div className="chart-summary-stat">
                    <span className="chart-summary-value">{uniqueMerchants}</span>
                    <span className="chart-summary-label">Merchants</span>
                </div>
                <div className="chart-summary-stat">
                    <span className="chart-summary-value">{uniqueBrands}</span>
                    <span className="chart-summary-label">Brands</span>
                </div>
                <div className="chart-summary-stat">
                    <span className="chart-summary-value" style={{ color: dataQuality.score >= 80 ? '#1a5f3c' : dataQuality.score >= 50 ? '#806000' : '#900' }}>{dataQuality.score}%</span>
                    <span className="chart-summary-label">Data Quality</span>
                </div>
                {reconRate !== null && (
                    <div className="chart-summary-stat">
                        <span className="chart-summary-value">{reconRate}%</span>
                        <span className="chart-summary-label">Reconciliation</span>
                    </div>
                )}
                {totalTokens > 0 && (
                    <div className="chart-summary-stat">
                        <span className="chart-summary-value">{totalTokens.toLocaleString()}</span>
                        <span className="chart-summary-label">AI Tokens</span>
                    </div>
                )}
            </div>
            {/* ── Date range slider ─────────────────────────────────── */}
            {onFilter && onClear && (
                <div className="chart-card">
                    <DateRangeSlider onFilter={onFilter} onClear={onClear} />
                </div>
            )}
            {/* ── Monthly spending trend ─────────────────────────────── */}
            {monthlySpending.length > 1 && (
                <div className="chart-card">
                    <span className="chart-title">Monthly Spending</span>
                    <ResponsiveContainer width="100%" height={210}>
                        <AreaChart data={monthlySpending}>
                            <defs>
                                <linearGradient id="spendGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#000" stopOpacity={0.25} />
                                    <stop offset="100%" stopColor="#000" stopOpacity={0.03} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
                            <XAxis dataKey="month" tick={AXIS_TICK} />
                            <YAxis tick={AXIS_TICK} />
                            <Tooltip {...TOOLTIP_STYLE} formatter={fmtCurrency(currencyCode)} />
                            <Area type="monotone" dataKey="total" stroke="#000" strokeWidth={2} fill="url(#spendGrad)" name="Spent" />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* ── Cumulative spending ────────────────────────────────── */}
            {cumulativeSpending.length > 1 && (
                <div className="chart-card">
                    <span className="chart-title">Cumulative Spending</span>
                    <ResponsiveContainer width="100%" height={200}>
                        <AreaChart data={cumulativeSpending}>
                            <defs>
                                <linearGradient id="cumGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#1a5f3c" stopOpacity={0.3} />
                                    <stop offset="100%" stopColor="#1a5f3c" stopOpacity={0.03} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
                            <XAxis dataKey="month" tick={AXIS_TICK} />
                            <YAxis tick={AXIS_TICK} />
                            <Tooltip {...TOOLTIP_STYLE} formatter={fmtCurrencyLabel(currencyCode, 'Total')} />
                            <Area type="monotone" dataKey="cumulative" stroke="#1a5f3c" strokeWidth={2} fill="url(#cumGrad)" name="Cumulative" />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* ── Category pie + Day of week bar (side-by-side on desktop) ── */}
            <div className="chart-row">
                {categorySpending.length > 0 && (
                    <div className="chart-card" style={{ flex: 1 }}>
                        <span className="chart-title">Spending by Category</span>
                        <ResponsiveContainer width="100%" height={250}>
                            <PieChart>
                                <Pie
                                    data={categorySpending}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={80}
                                    label={renderPieLabel}
                                    labelLine={{ stroke: 'rgba(0,0,0,0.25)' }}
                                    style={{ fontSize: '0.68rem', fontFamily: 'inherit' }}
                                >
                                    {categorySpending.map((entry, i) => (
                                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} style={{ cursor: 'pointer' }} onClick={() => navigate(`/Category?categoryId=${encodeURIComponent(entry.name)}`)} />
                                    ))}
                                </Pie>
                                <Tooltip {...TOOLTIP_STYLE} formatter={fmtCurrency(currencyCode)} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                )}

                {dayOfWeekSpending.some(d => d.total > 0) && (
                    <div className="chart-card" style={{ flex: 1 }}>
                        <span className="chart-title">Spending by Day of Week</span>
                        <ResponsiveContainer width="100%" height={250}>
                            <BarChart data={dayOfWeekSpending}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
                                <XAxis dataKey="day" tick={AXIS_TICK} />
                                <YAxis tick={AXIS_TICK} />
                                <Tooltip {...TOOLTIP_STYLE} formatter={((v: any, name: any) => [
                                    name === 'avg' ? `${currencyCode} ${Number(v).toLocaleString()}` : name === 'visits' ? v : `${currencyCode} ${Number(v).toLocaleString()}`,
                                    name === 'avg' ? 'Avg / Visit' : name === 'visits' ? 'Visits' : 'Total'
                                ]) as any} />
                                <Bar dataKey="total" fill="#000" radius={[6, 6, 0, 0]} name="total" />
                                <Bar dataKey="avg" fill="rgba(0,0,0,0.35)" radius={[6, 6, 0, 0]} name="avg" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>

            {/* ── Top merchants + Top brands (side-by-side on desktop) ────── */}
            <div className="chart-row">
                {topMerchants.length > 0 && (
                    <div className="chart-card" style={{ flex: 1 }}>
                        <span className="chart-title">Top Merchants by Spend</span>
                        <ResponsiveContainer width="100%" height={Math.max(200, topMerchants.length * 32)}>
                            <BarChart data={topMerchants} layout="vertical" margin={{ left: 10 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
                                <XAxis type="number" tick={AXIS_TICK} />
                                <YAxis type="category" dataKey="name" tick={AXIS_TICK} width={100} />
                                <Tooltip {...TOOLTIP_STYLE} formatter={fmtCurrency(currencyCode)} />
                                <Bar dataKey="total" fill="#000" radius={[0, 6, 6, 0]} name="Spent" style={{ cursor: 'pointer' }} onClick={(data: any) => data?.id && navigate(`/Merchant?merchantId=${encodeURIComponent(data.id)}`)} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}

                {topBrands.length > 0 && (
                    <div className="chart-card" style={{ flex: 1 }}>
                        <span className="chart-title">Top Brands by Spend</span>
                        <ResponsiveContainer width="100%" height={Math.max(200, topBrands.length * 32)}>
                            <BarChart data={topBrands} layout="vertical" margin={{ left: 10 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
                                <XAxis type="number" tick={AXIS_TICK} />
                                <YAxis type="category" dataKey="name" tick={AXIS_TICK} width={100} />
                                <Tooltip {...TOOLTIP_STYLE} formatter={fmtCurrency(currencyCode)} />
                                <Bar dataKey="total" fill="#1a5f3c" radius={[0, 6, 6, 0]} name="Spent" style={{ cursor: 'pointer' }} onClick={(data: any) => data?.fullName && navigate(`/Brand?brandId=${encodeURIComponent(data.fullName)}`)} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>

            {/* ── Receipt count + spend dual axis ────────────────────── */}
            {receiptsTrend.length > 1 && (
                <div className="chart-card">
                    <span className="chart-title">Receipts &amp; Spending / Month</span>
                    <ResponsiveContainer width="100%" height={200}>
                        <ComposedChart data={receiptsTrend}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
                            <XAxis dataKey="month" tick={AXIS_TICK} />
                            <YAxis yAxisId="left" tick={AXIS_TICK} />
                            <YAxis yAxisId="right" orientation="right" tick={AXIS_TICK} />
                            <Tooltip {...TOOLTIP_STYLE} formatter={((v: any, name: any) => [
                                name === 'receipts' ? v : `${currencyCode} ${Number(v).toLocaleString()}`,
                                name === 'receipts' ? 'Receipts' : 'Spent'
                            ]) as any} />
                            <Bar yAxisId="left" dataKey="receipts" fill="rgba(0,0,0,0.15)" radius={[4, 4, 0, 0]} name="receipts" />
                            <Line yAxisId="right" type="monotone" dataKey="total" stroke="#000" strokeWidth={2} dot={false} name="total" />
                            <Legend wrapperStyle={{ fontSize: '0.7rem', fontFamily: 'inherit' }} />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* ── Basket size + Currency (side-by-side) ──────────────── */}
            <div className="chart-row">
                {basketTrend.length > 1 && (
                    <div className="chart-card" style={{ flex: 1 }}>
                        <span className="chart-title">Avg Basket Size / Month</span>
                        <ResponsiveContainer width="100%" height={200}>
                            <AreaChart data={basketTrend}>
                                <defs>
                                    <linearGradient id="basketGrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#806000" stopOpacity={0.25} />
                                        <stop offset="100%" stopColor="#806000" stopOpacity={0.03} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
                                <XAxis dataKey="month" tick={AXIS_TICK} />
                                <YAxis tick={AXIS_TICK} />
                                <Tooltip {...TOOLTIP_STYLE} formatter={((v: any, name: any) => [
                                    name === 'avg' ? `${Number(v)} items` : `${v} total`,
                                    name === 'avg' ? 'Avg Items / Receipt' : 'Total Items'
                                ]) as any} />
                                <Area type="monotone" dataKey="avg" stroke="#806000" strokeWidth={2} fill="url(#basketGrad)" name="avg" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                )}

                {currencyBreakdown.length > 1 && (
                    <div className="chart-card" style={{ flex: 1 }}>
                        <span className="chart-title">Spending by Currency</span>
                        <ResponsiveContainer width="100%" height={200}>
                            <PieChart>
                                <Pie
                                    data={currencyBreakdown}
                                    dataKey="total"
                                    nameKey="currency"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={70}
                                    label={({ currency, total }: any) => `${currency} ${Number(total).toLocaleString()}`}
                                    labelLine={{ stroke: 'rgba(0,0,0,0.25)' }}
                                    style={{ fontSize: '0.68rem', fontFamily: 'inherit' }}
                                >
                                    {currencyBreakdown.map((_, i) => (
                                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip {...TOOLTIP_STYLE} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>

            {/* ── Data quality breakdown ──────────────────────────────── */}
            {dataQuality.breakdown.length > 0 && (
                <div className="chart-card">
                    <span className="chart-title">Data Quality Issues <span style={{ fontSize: '0.65rem', fontWeight: 400, textTransform: 'none', letterSpacing: 0, opacity: 0.5 }}>— {dataQuality.score}% clean — click to browse</span></span>
                    <ResponsiveContainer width="100%" height={Math.max(120, dataQuality.breakdown.length * 32)}>
                        <BarChart data={dataQuality.breakdown} layout="vertical" margin={{ left: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
                            <XAxis type="number" tick={AXIS_TICK} allowDecimals={false} />
                            <YAxis type="category" dataKey="issue" tick={AXIS_TICK} width={120} />
                            <Tooltip {...TOOLTIP_STYLE} />
                            <Bar dataKey="count" fill="#900" radius={[0, 6, 6, 0]} name="Issues" style={{ cursor: 'pointer' }} onClick={(data: any) => {
                                const tabMap: Record<string, string> = {
                                    'Missing brand': 'product-items',
                                    'Missing category': 'product-items',
                                    'Zero-total receipts': 'receipts',
                                    'Missing date': 'receipts',
                                    'Missing location': 'merchants',
                                    'Total mismatch': 'receipts',
                                };
                                const dqMap: Record<string, string> = {
                                    'Missing brand': 'missing-brand',
                                    'Missing category': 'missing-category',
                                    'Zero-total receipts': 'zero-total',
                                    'Missing date': 'missing-date',
                                    'Missing location': 'missing-location',
                                    'Total mismatch': 'total-mismatch',
                                };
                                const tab = tabMap[data?.issue];
                                const dq = dqMap[data?.issue];
                                if (tab) navigate(`/Search?tab=${tab}${dq ? `&dq=${dq}` : ''}`);
                            }} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* ── Orphaned entities ───────────────────────────────────── */}
            {dataQuality.orphans.length > 0 && (
                <div className="chart-card">
                    <span className="chart-title">Orphaned Records <span style={{ fontSize: '0.65rem', fontWeight: 400, textTransform: 'none', letterSpacing: 0, opacity: 0.5 }}>— entities with broken references — click to browse</span></span>
                    <ResponsiveContainer width="100%" height={Math.max(120, dataQuality.orphans.length * 32)}>
                        <BarChart data={dataQuality.orphans} layout="vertical" margin={{ left: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
                            <XAxis type="number" tick={AXIS_TICK} allowDecimals={false} />
                            <YAxis type="category" dataKey="issue" tick={AXIS_TICK} width={140} />
                            <Tooltip {...TOOLTIP_STYLE} />
                            <Bar dataKey="count" fill="#806000" radius={[0, 6, 6, 0]} name="Orphaned" style={{ cursor: 'pointer' }} onClick={(data: any) => {
                                const tabMap: Record<string, string> = {
                                    'Orphaned items': 'product-items',
                                    'Orphaned receipts': 'receipts',
                                    'Orphaned merchants': 'merchants',
                                    'Orphaned brands': 'brands-corps',
                                    'Orphaned corporations': 'corporations',
                                };
                                const dqMap: Record<string, string> = {
                                    'Orphaned items': 'orphaned-items',
                                    'Orphaned receipts': 'orphaned-receipts',
                                    'Orphaned merchants': 'orphaned-merchants',
                                    'Orphaned brands': 'orphaned-brands',
                                    'Orphaned corporations': 'orphaned-corporations',
                                };
                                const tab = tabMap[data?.issue];
                                const dq = dqMap[data?.issue];
                                if (tab) navigate(`/Search?tab=${tab}${dq ? `&dq=${dq}` : ''}`);
                            }} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}
        </>
    );
}
